import { Form, TextField, Button, Select, CircularProgress, Box, Fieldset } from "@okta/odyssey-react-mui";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers';
import { useCallback, useEffect, useState } from "react";
import { createEvent } from "../../services/EventsAPI";
import { useAuth0 } from "@auth0/auth0-react";
import ErrorMessage from "../callouts/ErrorMessage"
import { useTranslation } from "react-i18next";
import utc from "dayjs/plugin/utc";
import timezone from 'dayjs/plugin/timezone';
import dayjs from "dayjs";
import { useLabContext } from "../../LabContext";
import OpportunityPicker from "../fields/OpportunityPicker";
import { useNavigate } from 'react-router-dom';
import { useFlags, useFlagsmithLoading } from "flagsmith/react";

dayjs.extend(utc)
dayjs.extend(timezone);

const CreateEventForm = ({labId}) => {

    const { t } = useTranslation();
    const flags = useFlags(['arches_lab_mapping']);
    const flagsmithState = useFlagsmithLoading();
    const [eventType, setEventType] = useState('');
    const [opportunity, setOpportunity] = useState(undefined);
    const { user, getAccessTokenSilently } = useAuth0();
    const [error, setError] = useState();
    const [labContent, setLabContent] = useState(labId);
    const [isWaiting, setIsWaiting] = useState();
    const [timezone, setTimezone] = useState('UTC');
    const navigate = useNavigate();
    const labContext = useLabContext();

    const determineLabContent = useCallback(() => {
        if(labId && !flagsmithState.isLoading && !flagsmithState.isFetching && labContext.isReady) {
            const reversedMapping = Object.fromEntries(
                Object
                .entries(
                    JSON.parse(flags.arches_lab_mapping.value)
                )
                .map(([key, value]) => [value, key])
            )
            setLabContent(reversedMapping[labId] || labId);
            
        }}, [labId, flags.arches_lab_mapping, flagsmithState, labContext]);
        
    useEffect(() => determineLabContent(), [determineLabContent]);

    async function handleSubmit({target}){
        setIsWaiting(true)
        try{
            const start = dayjs(target.start.value).tz(timezone);
            const end = dayjs(target.end.value).tz(timezone);
            if (!end.isAfter(start)) {
                setError(t('createForm-endAfterStartError', { ns: 'events' }));
                setIsWaiting(false);
                return;
            }
            if(labContent === null || labContent === ''){
                setError(t('createForm-contentRequiredError', { ns: 'events' }));
                setIsWaiting(false);
                return;
            }
            const at = await getAccessTokenSilently()
            const resp = await createEvent(
                at,
                target.label.value,
                target.description.value,
                target.eventType.value,
                target.content.value,
                parseInt(target.participantCount.value),
                eventType === 'workshop' || eventType === 'poc' ? opportunity : undefined,
                start.utc().format(),
                end.utc().format(),
                user.email,
                []
            );
            setIsWaiting(false);
            navigate(`/events/${resp.data.eventCode}`, {replace:true});
        } catch(err){
            setError(err)
            setIsWaiting(false);
        }
    }
    
    function handleEventTypeChange(e) {
    setEventType(e.target.value);
    }

    function opportunityChanged(opportunityId) {
        setOpportunity(opportunityId);
    }

    return (<>
                <Form
                    name="create-event-form"
                    onSubmit={(event)=>{event.preventDefault();handleSubmit(event)}}
                    autoCompleteType='on' 
                    formActions={
                        <Box sx={{display:'flex', alignItems: 'center'}}>
                        {isWaiting && <CircularProgress/>}
                        <Button isDisabled={isWaiting} label={t('cancel')} variant='floating' type='reset' onClick={()=>navigate('/events')} />
                        <Button isDisabled={isWaiting} label={t('submit')} variant='primary' type='submit' />
                        </Box>
                    }>
                    <TextField name="label" label={t('createForm-eventTitle',{ns:'events'})} hint={t('createForm-eventTitleHint',{ns:'events'})}/>
                    <TextField name="description" label={t('createForm-eventDescription',{ns:'events'})} isMultiline={true} hint={t('createForm-eventDescriptionHint',{ns:'events'})}/>
                    <Select name="eventType"
                        label={t('createForm-eventType',{ns:'events'})}
                        hint={t('createForm-eventTypeHint',{ns:'events'})}
                        defaultValue={'pipeline'}
                        options={[
                            {text: t('eventTypeLabel-pipeline',{ns:'events'}), value:'pipeline'},
                            {text: t('eventTypeLabel-workshop',{ns:'events'}), value:'workshop'},
                            {text: t('eventTypeLabel-poc',{ns:'events'}), value:'poc'},
                            {text: t('eventTypeLabel-internal',{ns:'events'}), value: 'internal'},
                            {text: t('eventTypeLabel-recruiting',{ns:'events'}), value: 'recruiting'}]}
                        onChange={handleEventTypeChange}/>
                    { (eventType === 'workshop' || eventType === 'poc') && (
                        <OpportunityPicker allowEdit={true} selectionChanged={opportunityChanged}/>
                        )
                    }
                    
                    <Fieldset legend={t('createForm-contentFieldSetHeading',{ns:'events'})} description={t('createForm-contentFieldSetDescription',{ns:'events'})}>
                        <Select
                        name="content"
                        label={t('createForm-content',{ns:'events'})}
                        hint={t('createForm-contentHint',{ns:'events'})}
                        options={labContext.labs.filter((lab)=> lab.state === 'active').map((lab)=>{return {value:lab.id, text:lab.title}})}
                        value={labContent}
                        onChange={e => setLabContent(e.target.value)}
                        />
                        <TextField name="participantCount" label={t('createForm-participantCount',{ns:'events'})} hint={t('createForm-participantCountHint',{ns:'events'})} type='number'/>
                    </Fieldset>

                    <Fieldset legend={t('createForm-dateFieldSetHeading',{ns:'events'})} description={t('createForm-dateFieldSetDescription',{ns:'events'})}>
                        <Select
                            name="timezone"
                            label={t('createForm-timezone',{ns:'events'})}
                            options={[
                                { text: t('timezone-utc', { ns: 'events' }), value: 'UTC' },
                                { text: t('timezone-tokyo', { ns: 'events' }), value: 'Asia/Tokyo' },
                                { text: t('timezone-singapore', { ns: 'events' }), value: 'Asia/Singapore' },
                                { text: t('timezone-sydney', { ns: 'events' }), value: 'Australia/Sydney' },
                                { text: t('timezone-bengaluru', { ns: 'events' }), value: 'Asia/Kolkata' },
                                { text: t('timezone-paris', { ns: 'events' }), value: 'Europe/Paris' },
                                { text: t('timezone-stockholm', { ns: 'events' }), value: 'Europe/Stockholm' },
                                { text: t('timezone-london', { ns: 'events' }), value: 'Europe/London' },
                                { text: t('timezone-newyork', { ns: 'events' }), value: 'America/New_York' },
                                { text: t('timezone-toronto', { ns: 'events' }), value: 'America/Toronto' },
                                { text: t('timezone-sanfrancisco', { ns: 'events' }), value: 'America/Los_Angeles' },
                            ]}
                            defaultValue="UTC"
                            value={timezone}
                            onChange={e => setTimezone(e.target.value)}
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker name="start" label='Event Start' timezone={timezone}/>
                            <DateTimePicker name="end" label='Event End' timezone={timezone}/>
                        </LocalizationProvider>
                    </Fieldset>
                    { error && <ErrorMessage error={error} /> }
            </Form>
    </>)
}

export default CreateEventForm